import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { QRCodeCanvas } from 'qrcode.react';
import { ShieldCheck, Copy, CheckCircle2, ChevronRight, AlertCircle } from 'lucide-react';

/**
 * MFASetup — walks admin through enrolling TOTP MFA.
 * Step 1: Show QR code + backup codes
 * Step 2: Confirm with first TOTP code
 * Step 3: Success
 */
function MFASetup({ user, onUserUpdate }) {
  const [step, setStep] = useState('loading'); // loading | setup | confirm | success
  const [otpauthUri, setOtpauthUri] = useState('');
  const [backupCodes, setBackupCodes] = useState([]);
  const [confirmCode, setConfirmCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [copiedBackup, setCopiedBackup] = useState(false);
  const confirmRef = useRef(null);
  const setupCalledRef = useRef(false);
  const navigate = useNavigate();
  const token = localStorage.getItem('token');

  useEffect(() => {
    if (!token) {
      navigate('/tf-admin-auth', { replace: true });
      return;
    }
    // Guard against React StrictMode double-invoke
    if (setupCalledRef.current) return;
    setupCalledRef.current = true;
    initSetup();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const initSetup = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/mfa/setup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (response.ok) {
        setOtpauthUri(data.otpauth_uri);
        setBackupCodes(data.backup_codes);
        setStep('setup');
      } else {
        if (data.detail === 'MFA is already enabled for this account') {
          setStep('already_enabled');
        } else {
          toast.error(data.detail || 'Failed to initialize MFA setup');
          navigate('/browse', { replace: true });
        }
      }
    } catch {
      toast.error('Connection error during MFA setup');
      navigate('/browse', { replace: true });
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async (e) => {
    e.preventDefault();
    const code = confirmCode.trim();
    if (code.length !== 6 || !/^\d{6}$/.test(code)) {
      toast.error('Please enter the 6-digit code from your authenticator app');
      return;
    }
    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/mfa/confirm`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ code }),
      });
      const data = await response.json();
      if (response.ok) {
        setStep('success');
        if (onUserUpdate) onUserUpdate({ ...user, mfa_enabled: true });
        toast.success('MFA enabled successfully!');
      } else {
        toast.error(data.detail || 'Verification failed. Please try again.');
        setConfirmCode('');
        if (confirmRef.current) confirmRef.current.focus();
      }
    } catch {
      toast.error('Connection error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const copyBackupCodes = () => {
    navigator.clipboard.writeText(backupCodes.join('\n')).then(() => {
      setCopiedBackup(true);
      setTimeout(() => setCopiedBackup(false), 2500);
    });
  };

  if (step === 'loading') {
    return (
      <div style={pageStyle}>
        <div style={cardStyle}>
          <div style={{ textAlign: 'center', padding: '48px' }}>
            <div style={spinnerStyle} />
            <p style={{ color: '#6B7280', marginTop: '16px' }}>Preparing MFA setup...</p>
          </div>
        </div>
      </div>
    );
  }

  if (step === 'already_enabled') {
    return (
      <div style={pageStyle}>
        <div style={cardStyle}>
          <div style={{ textAlign: 'center', padding: '48px' }}>
            <CheckCircle2 size={48} color="#16A34A" style={{ margin: '0 auto 16px' }} />
            <h2 style={{ fontSize: '22px', fontWeight: '700', color: '#1F2937', marginBottom: '12px' }}>MFA Already Active</h2>
            <p style={{ color: '#6B7280', marginBottom: '24px' }}>Your account already has two-factor authentication enabled.</p>
            <button onClick={() => navigate('/browse')} style={btnPrimary}>Go to Browse</button>
          </div>
        </div>
      </div>
    );
  }

  if (step === 'success') {
    return (
      <div style={pageStyle}>
        {headerEl}
        <main style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px 24px' }}>
          <div style={{ ...cardStyle, textAlign: 'center', padding: '48px 40px' }}>
            <div style={{ width: '80px', height: '80px', borderRadius: '50%', backgroundColor: '#DCFCE7', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px' }}>
              <ShieldCheck size={40} color="#16A34A" />
            </div>
            <h1 style={{ fontSize: '26px', fontWeight: '700', color: '#1F2937', marginBottom: '12px' }}>MFA Enabled!</h1>
            <p style={{ color: '#4B5563', lineHeight: '1.6', marginBottom: '32px', maxWidth: '380px', margin: '0 auto 32px' }}>
              Your admin account is now protected with two-factor authentication. You'll be asked for a code from your authenticator app on every login.
            </p>
            <button onClick={() => navigate('/browse')} style={btnPrimary} data-testid="mfa-setup-done-btn">
              Continue to Portal
            </button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div style={pageStyle}>
      {headerEl}
      <main style={{ flex: 1, padding: '32px 24px', overflowY: 'auto' }}>
        <div style={{ maxWidth: '560px', margin: '0 auto' }}>
          {/* Progress indicator */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '28px' }}>
            {['Scan QR Code', 'Save Backup Codes', 'Confirm Setup'].map((label, i) => (
              <React.Fragment key={i}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <div style={{
                    width: '24px', height: '24px', borderRadius: '50%', fontSize: '12px', fontWeight: '600',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    backgroundColor: step === 'confirm' && i < 2 ? '#1B5E5F' : step === 'confirm' && i === 2 ? '#1B5E5F' : i === 0 ? '#1B5E5F' : '#D1D5DB',
                    color: (step === 'confirm' && i < 2) || (i === 0) ? '#FFF' : step === 'confirm' && i === 2 ? '#FFF' : '#6B7280',
                  }}>
                    {step === 'confirm' && i < 2 ? '✓' : i + 1}
                  </div>
                  <span style={{ fontSize: '12px', color: '#6B7280', display: window.innerWidth > 480 ? 'inline' : 'none' }}>{label}</span>
                </div>
                {i < 2 && <div style={{ flex: 1, height: '2px', backgroundColor: step === 'confirm' && i < 2 ? '#1B5E5F' : '#E5E7EB' }} />}
              </React.Fragment>
            ))}
          </div>

          {step === 'setup' && (
            <>
              {/* Step 1 & 2: QR + Backup Codes */}
              <div style={{ ...cardStyle, padding: '32px' }}>
                <h1 style={{ fontSize: '22px', fontWeight: '700', color: '#1F2937', marginBottom: '8px' }}>
                  Set Up Two-Factor Authentication
                </h1>
                <p style={{ fontSize: '14px', color: '#6B7280', marginBottom: '28px', lineHeight: '1.6' }}>
                  Scan the QR code below with <strong>Microsoft Authenticator</strong> or <strong>Google Authenticator</strong>, then save your backup codes.
                </p>

                {/* QR Code */}
                <div style={{ textAlign: 'center', marginBottom: '28px' }}>
                  <div style={{ display: 'inline-block', padding: '16px', backgroundColor: '#F9FAFB', borderRadius: '12px', border: '2px solid #E5E7EB' }}>
                    {otpauthUri && <QRCodeCanvas value={otpauthUri} size={200} level="H" data-testid="mfa-qr-code" />}
                  </div>
                  <p style={{ fontSize: '12px', color: '#6B7280', marginTop: '12px' }}>
                    Open your authenticator app → Add account → Scan QR code
                  </p>
                </div>

                {/* Manual entry hint */}
                <details style={{ marginBottom: '28px' }}>
                  <summary style={{ fontSize: '13px', color: '#1B5E5F', cursor: 'pointer', fontWeight: '500' }}>
                    Can't scan the QR code? Enter manually
                  </summary>
                  <div style={{ marginTop: '12px', padding: '12px', backgroundColor: '#F0FDF4', borderRadius: '8px', border: '1px solid #BBF7D0' }}>
                    <p style={{ fontSize: '12px', color: '#166534', margin: '0 0 8px', fontWeight: '500' }}>Manual entry in your authenticator app:</p>
                    <p style={{ fontSize: '11px', color: '#166534', margin: '0 0 4px' }}><strong>Account:</strong> {user?.email || ''}</p>
                    <p style={{ fontSize: '11px', color: '#166534', margin: '0 0 4px' }}><strong>Issuer:</strong> Compete Content Hub</p>
                    <p style={{ fontSize: '11px', color: '#166534', margin: 0 }}><strong>Secret:</strong> Found in the QR code — use your app's "Enter key manually" option to scan it.</p>
                  </div>
                </details>

                {/* Backup Codes */}
                <div style={{ backgroundColor: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: '10px', padding: '20px', marginBottom: '24px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <AlertCircle size={16} color="#D97706" />
                      <span style={{ fontSize: '14px', fontWeight: '600', color: '#92400E' }}>Save Your Backup Codes</span>
                    </div>
                    <button
                      onClick={copyBackupCodes}
                      style={{ background: 'none', border: '1px solid #D97706', borderRadius: '6px', padding: '4px 10px', cursor: 'pointer', color: '#92400E', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '4px' }}
                      data-testid="copy-backup-codes-btn"
                    >
                      {copiedBackup ? <><CheckCircle2 size={12} /> Copied!</> : <><Copy size={12} /> Copy all</>}
                    </button>
                  </div>
                  <p style={{ fontSize: '12px', color: '#92400E', marginBottom: '12px' }}>
                    Store these codes somewhere safe. Each code works once. Use them if you lose access to your phone.
                  </p>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px' }}>
                    {backupCodes.map((code, i) => (
                      <div key={i} style={{ fontFamily: 'monospace', fontSize: '13px', fontWeight: '600', color: '#1F2937', backgroundColor: '#FFF', padding: '6px 10px', borderRadius: '6px', border: '1px solid #FDE68A', letterSpacing: '2px' }}>
                        {code}
                      </div>
                    ))}
                  </div>
                </div>

                <button
                  onClick={() => { setStep('confirm'); setTimeout(() => { if (confirmRef.current) confirmRef.current.focus(); }, 100); }}
                  style={btnPrimary}
                  data-testid="mfa-next-btn"
                >
                  I've saved my backup codes <ChevronRight size={16} style={{ marginLeft: '4px' }} />
                </button>
              </div>
            </>
          )}

          {step === 'confirm' && (
            <div style={{ ...cardStyle, padding: '32px' }}>
              <h2 style={{ fontSize: '22px', fontWeight: '700', color: '#1F2937', marginBottom: '8px' }}>Confirm Your Setup</h2>
              <p style={{ fontSize: '14px', color: '#6B7280', marginBottom: '28px', lineHeight: '1.6' }}>
                Open your authenticator app and enter the current 6-digit code for <strong>Compete Content Hub</strong> to complete setup.
              </p>

              <form onSubmit={handleConfirm} data-testid="mfa-confirm-form">
                <div style={{ marginBottom: '24px' }}>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#1F2937', marginBottom: '8px' }}>
                    6-Digit Code from Authenticator App
                  </label>
                  <input
                    ref={confirmRef}
                    type="text"
                    inputMode="numeric"
                    value={confirmCode}
                    onChange={(e) => setConfirmCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    maxLength={6}
                    placeholder="000000"
                    autoComplete="one-time-code"
                    data-testid="mfa-confirm-code-input"
                    style={{
                      width: '100%',
                      padding: '14px 16px',
                      border: '2px solid #D1D5DB',
                      borderRadius: '8px',
                      fontSize: '28px',
                      fontFamily: 'monospace',
                      letterSpacing: '10px',
                      textAlign: 'center',
                      color: '#1F2937',
                      outline: 'none',
                      boxSizing: 'border-box',
                    }}
                    onFocus={(e) => { e.target.style.borderColor = '#1B5E5F'; }}
                    onBlur={(e) => { e.target.style.borderColor = '#D1D5DB'; }}
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading || confirmCode.length !== 6}
                  style={{ ...btnPrimary, opacity: loading || confirmCode.length !== 6 ? 0.6 : 1, cursor: loading || confirmCode.length !== 6 ? 'not-allowed' : 'pointer' }}
                  data-testid="mfa-confirm-submit"
                >
                  {loading ? 'Verifying...' : 'Enable MFA'}
                </button>
              </form>

              <button
                onClick={() => setStep('setup')}
                style={{ marginTop: '16px', background: 'none', border: 'none', color: '#1B5E5F', fontSize: '13px', cursor: 'pointer', fontWeight: '500' }}
              >
                Back to QR code
              </button>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

// Styles
const pageStyle = { minHeight: '100vh', backgroundColor: '#1B5E5F', display: 'flex', flexDirection: 'column' };
const cardStyle = { backgroundColor: '#FFFFFF', borderRadius: '12px', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' };
const btnPrimary = {
  width: '100%', padding: '14px', backgroundColor: '#1B5E5F', color: '#FFFFFF', border: 'none',
  borderRadius: '8px', fontSize: '15px', fontWeight: '600', cursor: 'pointer',
  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px',
};
const spinnerStyle = {
  width: '40px', height: '40px', border: '4px solid #E5E7EB', borderTopColor: '#1B5E5F',
  borderRadius: '50%', margin: '0 auto', animation: 'spin 1s linear infinite',
};
const headerEl = (
  <header style={{ backgroundColor: '#1B5E5F', borderBottom: '1px solid rgba(255,255,255,0.1)', padding: '16px 0' }}>
    <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <img src="/technofocus-logo.png" alt="Technofocus" style={{ height: '20px', filter: 'brightness(0) invert(1)' }} />
        <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: '20px', fontWeight: '200' }}>|</span>
        <img src="/levelup-logo-light.svg" alt="LevelUp" style={{ height: '20px' }} />
      </div>
      <span style={{ backgroundColor: 'rgba(255,255,255,0.2)', color: '#FFF', fontSize: '11px', fontWeight: '600', padding: '4px 10px', borderRadius: '12px', textTransform: 'uppercase' }}>
        MFA Setup
      </span>
    </div>
  </header>
);

export default MFASetup;
